// 公開ゲーム（生成ゲーム）の federation 既定（複数ゲームの共有実装案 §5 / §29.5）。
//
// runtime-config の systems.federation は「どの Platform Shell に埋め込まれることを許すか」を決める。
// これは実質的な信頼境界で、許可した origin の親ページは訪問初期化（Traveler の注入）を送れる。
// エンジンは grant の署名までは検証しないため、origin allowlist が唯一の関門になる。
//
// 開発用の allowLoopbackShell がそのまま公開ゲームへ乗ると、ローカルで動く任意のページが
// その公開ゲームを Shell として駆動できてしまうため、生成時と公開時に必ず落とす。
// あわせて shellOrigins も作者編集を無視してプラットフォーム既定へ固定する
// （runtime-config は作者が編集できるデータとして公開されるため）。
//
// エンジン側にも「自分自身が loopback で配信されているときだけ loopback Shell を許す」判定があり、
// 仮にこの既定を通り抜けた設定が残っても、公開ホストのゲームでは loopback Shell が成立しない。
// 二重にしているのは、どちらか一方の取りこぼしで信頼境界が開かないようにするため。

/**
 * Platform Shell の正規 origin。ここに無い origin からの訪問初期化は受け付けない。
 *
 * Shell は create ホストの `/play/` で配信し、ゲームは games ホストで開く。
 * 別 origin にしているのは iframe sandbox を隔離として効かせるためで、
 * 同一 origin だと frame 自身が sandbox 属性を外せてしまう（設計書 §5）。
 */
export const PLATFORM_SHELL_ORIGINS = ['https://create.voxelnovels.com'];

export const FEDERATION_OPT_IN_COMMENT = '公開ゲームの既定は「プラットフォームの Platform Shell からのみ訪問を受け付ける」。'
    + 'allowLoopbackShell はローカル検証専用のため公開時に必ず false へ落とす。'
    + 'shellOrigins も作者編集を反映せずプラットフォーム既定へ固定する（許可 origin = 訪問初期化を送れる相手）。'
    + 'このゲームへの訪問を止めたいときは enabled を false にする。';

/**
 * 生成ゲームの runtime-config オブジェクトへ federation 既定を適用する（in-place）。
 * - systems.federation.allowLoopbackShell を false にする
 * - systems.federation.shellOrigins をプラットフォーム既定へ固定する
 * - enabled は触らない（作者・運営が使うゲーム単位の停止スイッチのため）
 * federation キー自体が無い・型が不正な場合は何もしない（キー欠損=現行動作の契約を壊さない）。
 * @returns {{changed: boolean, disabledLoopback: boolean, shellOriginsBefore: string[]|null, shellOriginsAfter: string[]|null}}
 */
export function applyPublicGameFederationDefaults(runtimeConfig) {
    const result = { changed: false, disabledLoopback: false, shellOriginsBefore: null, shellOriginsAfter: null };
    const federation = runtimeConfig?.systems?.federation;
    if (!federation || typeof federation !== 'object' || Array.isArray(federation)) return result;

    if (federation.allowLoopbackShell !== false) {
        federation.allowLoopbackShell = false;
        result.disabledLoopback = true;
        result.changed = true;
    }

    const before = Array.isArray(federation.shellOrigins) ? [...federation.shellOrigins] : null;
    result.shellOriginsBefore = before;
    const expected = [...PLATFORM_SHELL_ORIGINS];
    if (JSON.stringify(before) !== JSON.stringify(expected)) {
        federation.shellOrigins = expected;
        result.changed = true;
    }
    result.shellOriginsAfter = [...expected];

    if (result.changed) {
        federation._federationDefaultsComment = FEDERATION_OPT_IN_COMMENT;
    }
    return result;
}
